import classNames from 'classnames'
import { Fragment, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import BlockFilters from '../../../components/Desktop/BlockFilters'
import BlockOrdering from '../../../components/Desktop/BlockOrdering'
import Search from '../../../assets/search.svg?react'
import Camera from '../../../assets/camera.svg?react'
import CloseSecondary from '../../../assets/close-secondary.svg?react'
import Map from '../../../assets/map.svg?react'
import ListIcon from '../../../assets/list.svg?react'
import MapActive from '../../../assets/map-active.svg?react'
import ListIconActive from '../../../assets/list-active.svg?react'
import LegendIcon from '../../../assets/legend-primary.svg?react'
import Layout from '../../../components/Desktop/Layout'
import { useQsFilters } from '../../../hooks/filters'
import styles from './List.module.css'
import { useInfiniteMomuments } from '../../../hooks/monuments'
import { Waypoint } from 'react-waypoint'
import IconMonument from '../../../components/IconMonument'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import Detail from '../../Mobile/Detail'
import {
  getActiveGeocontext,
  getLabelFromSlug,
  parseSmartSlug,
} from '../../../utils'
import { useTranslation } from 'react-i18next'
import { useTopContextState } from '../../../context/TopContext'
import { usePaeseContext } from '../../../context/PaeseContext'
import Legend from '../../../components/Desktop/Legend'

const getFilters = (params: URLSearchParams) => ({
  search: params.get('search') ?? '',
  municipality: params.get('municipality') ?? '',
  province: params.get('province') ?? '',
  region: params.get('region') ?? '',
  ordering: params.get('ordering') ?? '',
  in_contest: params.get('in_contest') ?? 'true',
  only_without_pictures: params.get('only_without_pictures') ?? '',
  app_category: params.get('app_category') ?? '',
  user_lat: params.get('user_lat') ?? '',
  user_lon: params.get('user_lon') ?? '',
  monument_id: params.get('monument_id') ?? '',
  monument_lat: params.get('monument_lat') ?? '',
  monument_lon: params.get('monument_lon') ?? '',
  cluster_active: params.get('cluster_active') ?? '',
  geo_context: params.get('geo_context') ?? '',
  only_disabled: params.get('only_disabled') ?? '',
  without_coordinates: params.get('without_coordinates') ?? '',
  last_image_before_5_years: params.get('last_image_before_5_years') ?? '',
})

interface Props {
  filters: {
    search: string
    province: string
    region: string
    municipality: string
    ordering: string
    app_category: string
    in_contest: string
    only_without_pictures: string
    user_lat: string
    user_lon: string
    monument_lat: string
    monument_lon: string
    monument_id: string
    geo_context: string
    only_disabled: string
    without_coordinates: string
    last_image_before_5_years: string
  }
  setDetail: (monument: number | null) => void
  detail: number | null
  setFilters: (filters: any) => void
}

export function ListMonuments({
  filters,
  setDetail,
  detail,
  setFilters,
}: Props) {
  const filtersForMonument = useMemo(() => {
    return {
      search: filters.search,
      municipality: filters.municipality,
      province: filters.province,
      region: filters.region,
      ordering: filters.ordering,
      app_category: filters.app_category,
      in_contest: filters.in_contest,
      only_without_pictures: filters.only_without_pictures,
      user_lat: filters.user_lat,
      user_lon: filters.user_lon,
      geo_context: filters.geo_context,
      only_disabled: filters.only_disabled,
      without_coordinates: filters.without_coordinates,
      last_image_before_5_years: filters.last_image_before_5_years,
    }
  }, [filters])

  const enableQuery = useMemo(() => {
    if (filters.ordering === '') {
      return false
    }
    return (
      filters.ordering !== 'distance' ||
      (filters.ordering === 'distance' &&
        !!filters.user_lat &&
        !!filters.user_lon)
    )
  }, [filters])

  const {
    data: infiniteMonuments,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    fetchNextPage,
  } = useInfiniteMomuments(filtersForMonument, enableQuery)

  useEffect(() => {
    if (history.state?.scroll) {
      listMonumentsRef.current!.scrollTop = history.state.scroll
    } else {
      listMonumentsRef.current!.scrollTop = 0
    }
  }, [])

  const [isLoadingPosition, setIsLoadingPosition] = useState(false)

  const listMonumentsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (filters.monument_id !== '') {
      setDetail(Number(filters.monument_id))
    } else {
      setDetail(null)
    }
  }, [filters.monument_id])

  const { geoPermission } = useTopContextState()

  function success(position: any) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    setFilters({
      ...filters,
      ordering: 'distance',
      user_lat: latitude,
      user_lon: longitude,
    })
    setIsLoadingPosition(false)
  }

  function error() {
    setIsLoadingPosition(false)
    setFilters({
      ...filters,
      ordering: 'label',
    })
    console.log('Unable to retrieve your location')
  }

  useEffect(() => {
    if (
      navigator.geolocation &&
      geoPermission !== 'denied' &&
      filters.ordering === ''
    ) {
      setIsLoadingPosition(true)
      navigator.geolocation.getCurrentPosition(success, error)
    } else {
      if (filters.ordering === '') {
        setFilters({
          ...filters,
          ordering: 'label',
        })
      }
    }
  }, [])

  const { t, i18n } = useTranslation()

  const { country, setCountry } = usePaeseContext()
  const { geocontexts, isLoading: isLoadingGeocontexts } = useTopContextState()
  const navigation = useNavigate()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const geoContextId = urlParams.get('geo_context')
    if (geocontexts.length > 0 && geoContextId) {
      const geocontext = geocontexts.find(
        (context) => context.id === parseInt(geoContextId)
      )
      if (geocontext) {
        setCountry({
          id: geocontext.id,
          label: geocontext.label,
        })
        localStorage.setItem(
          'paese',
          JSON.stringify({
            id: geocontext.id,
            label: geocontext.label,
          })
        )
        localStorage.setItem(
          'trigger_paese',
          JSON.stringify({
            id: geocontext.id,
            label: geocontext.label,
          })
        )
      }
    }
    if (!country?.id && !isLoadingGeocontexts) {
      navigation('/' + i18n.language + '/')
    }
  }, [country?.id, geocontexts, isLoadingGeocontexts])

  return (
    <div className={classNames(styles.ListMonuments)} ref={listMonumentsRef}>
      {(isFetching && !isFetchingNextPage) || isLoadingPosition || isLoading ? (
        <div className="d-flex align-items-center justify-content-center w-100 h-100">
          <div className="loader" />
        </div>
      ) : (
        infiniteMonuments &&
        infiniteMonuments?.pages.map((list, i) => (
          <Fragment key={i}>
            {list.results.length > 0 &&
              list.results.map((monument, k) => {
                return (
                  <div
                    key={k}
                    className={classNames({
                      [styles.DisabledEntry]: monument.disabled,
                      [styles.MonumentCard]: !detail || detail !== monument.id,
                      [styles.MonumentCardWithDetail]:
                        detail && detail === monument.id,
                    })}
                    onClick={() => {
                      setDetail(monument.id)
                      setFilters({
                        ...filters,
                        monument_id: monument.id,
                        monument_lat: monument.position?.coordinates[1],
                        monument_lon: monument.position?.coordinates[0],
                      })
                    }}
                  >
                    {monument.disabled && (
                      <div className={styles.DisabledEntryLabel}>
                        {t('GENERIC_DISABLED')}
                      </div>
                    )}
                    <div className="d-flex">
                      <div>
                        <IconMonument monument={monument} />
                      </div>
                      <div className="ms-2">
                        <div className={styles.MonumentTitle}>
                          {monument.label.charAt(0).toUpperCase() +
                            monument.label.slice(1)}
                        </div>
                        <div className={styles.City}>
                          {monument.municipality_label}
                          {monument.location &&
                            monument.location !==
                              monument.municipality_label && (
                              <div>
                                {t('MPL_BOX_LOCATION')}: {monument.location}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center flex-column">
                      <div className={styles.NumberPhoto}>
                        <div>{monument.pictures_count}</div>
                        <Camera className="ms-2" />
                      </div>
                      {monument.distance && geoPermission === 'granted' && (
                        <div className={styles.Distance}>
                          {monument.distance.toFixed(1)} km
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            {list.results.length === 0 && (
              <div className={styles.NessunRisultato}>
                {t('MPL_BOX_NO_RESULTS_FOUND')}
              </div>
            )}
          </Fragment>
        ))
      )}
      {hasNextPage && !isLoading && (
        <Waypoint
          topOffset={-100}
          onEnter={() => {
            fetchNextPage()
          }}
        />
      )}
    </div>
  )
}

export default function List() {
  const { uiFilters, filters, setFilters, setFiltersDebounced } =
    useQsFilters(getFilters)
  const [detail, setDetail] = useState<number | null>(null)

  const { slug } = useParams()

  useEffect(() => {
    if (slug) {
      setDetail(Number(parseSmartSlug(slug)))
      setFilters({
        ...filters,
        search: getLabelFromSlug(slug),
      })
    }
  }, [slug])

  const navigate = useNavigate()

  const { i18n, t } = useTranslation()
  const location = useLocation()
  const currentPath = location.pathname

  const [showLegend, setShowLegend] = useState(false)

  const MIN_FILTERS_WIDTH = 50
  const MAX_FILTERS_WIDTH = 500
  const DEFAULT_FILTERS_WIDTH = 340

  const [filtersWidth, setFiltersWidth] = useState<number>(() => {
    const savedWidth = localStorage.getItem('desktop_filters_width')
    return savedWidth ? Number(savedWidth) : DEFAULT_FILTERS_WIDTH
  })

  const [isResizing, setIsResizing] = useState(false)
  const layoutRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    localStorage.setItem('desktop_filters_width', String(filtersWidth))
  }, [filtersWidth])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizing || !layoutRef.current) return

      const containerRect = layoutRef.current.getBoundingClientRect()
      const nextWidth = event.clientX - containerRect.left

      const clampedWidth = Math.min(
        MAX_FILTERS_WIDTH,
        Math.max(MIN_FILTERS_WIDTH, nextWidth)
      )

      setFiltersWidth(clampedWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  return (
    <Layout>
      <div className="d-flex h-100 w-100" ref={layoutRef}>
        <div
          className={styles.FiltersColumn}
          style={{
            width: `${filtersWidth}px`,
            minWidth: `${filtersWidth}px`,
            transition: isResizing ? 'none' : 'width 0.3s ease-in-out',
          }}
        >
          <BlockFilters
            filters={filters}
            setFilters={setFilters}
            setDetail={setDetail}
            isResized={filtersWidth <= DEFAULT_FILTERS_WIDTH - 100}
            setFiltersWidth={setFiltersWidth}
          />
        </div>

        <div
          className={styles.ResizeHandle}
          onMouseDown={() => setIsResizing(true)}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ridimensiona colonna filtri"
        />

        <div className={styles.ListContainer}>
          <div
            className={styles.CardContainerList}
            style={{
              width: 'calc(100%)',
              height: '100%',
              transition: isResizing ? 'none' : 'width 0.5s ease-in-out',
            }}
          >
            <div
              className={classNames({
                [styles.MonumentsBlock]: !detail,
                [styles.MonumentsBlockWithDetail]: detail,
              })}
            >
              <div className="w-100 position-relative d-flex align-items-center">
                <div
                  className={styles.ButtonLegend}
                  onClick={() => setShowLegend(!showLegend)}
                >
                  <LegendIcon />
                </div>

                <div className={styles.SwitchMapList}>
                  <Link
                    className={classNames({
                      [styles.SwitchItem]: true,
                    })}
                    to={`/${i18n.language}/mappa?${new URLSearchParams({
                      search: filters.municipality ? filters.search : '',
                      municipality: filters.municipality,
                      province: filters.province,
                      region: filters.region,
                      app_category: filters.app_category,
                      in_contest: filters.in_contest,
                      only_without_pictures: filters.only_without_pictures,
                      user_lat: filters.user_lat || '',
                      user_lon: filters.user_lon || '',
                      ordering: filters.ordering,
                      monument_id: filters.monument_id || '',
                      monument_lat: filters.monument_lat || '',
                      monument_lon: filters.monument_lon || '',
                      cluster_active: filters.cluster_active,
                      geo_context:
                        filters.geo_context ||
                        String(getActiveGeocontext()?.id),
                      only_disabled: filters.only_disabled,
                      without_coordinates: filters.without_coordinates,
                      last_image_before_5_years:
                        filters.last_image_before_5_years,
                    })}`}
                  >
                    {currentPath === '/' + i18n.language + '/' ||
                    currentPath === '/' ||
                    currentPath.indexOf('mappa') !== -1 ? (
                      <MapActive className="me-1" />
                    ) : (
                      <Map className="me-1" />
                    )}
                    {t('MPL_BOX_MAP')}
                  </Link>
                  <Link
                    to={`/${i18n.language}/lista?${new URLSearchParams({
                      search: filters.search,
                      municipality: filters.municipality,
                      province: filters.province,
                      region: filters.region,
                      app_category: filters.app_category,
                      in_contest: filters.in_contest,
                      only_without_pictures: filters.only_without_pictures,
                      user_lat: filters.user_lat || '',
                      user_lon: filters.user_lon || '',
                      ordering: filters.ordering,
                      monument_id: filters.monument_id || '',
                      monument_lat: filters.monument_lat || '',
                      monument_lon: filters.monument_lon || '',
                      cluster_active: filters.cluster_active,
                      geo_context:
                        filters.geo_context ||
                        String(getActiveGeocontext()?.id),
                      only_disabled: filters.only_disabled,
                      without_coordinates: filters.without_coordinates,
                      last_image_before_5_years:
                        filters.last_image_before_5_years,
                    })}`}
                    className={classNames({
                      [styles.SwitchItemActive]: true,
                    })}
                  >
                    {currentPath === '/' + i18n.language + '/lista' ||
                    currentPath.indexOf('lista') !== -1 ? (
                      <ListIconActive className="me-1" />
                    ) : (
                      <ListIcon className="me-1" />
                    )}
                    {t('MPL_BOX_LIST')}
                  </Link>
                </div>

                <div className="position-relative w-100">
                  <input
                    onChange={(e) => {
                      setFiltersDebounced({ search: e.target.value })
                    }}
                    value={uiFilters.search}
                    className={styles.InputSearch}
                    type="text"
                  />
                  <div className={styles.SearchIcon}>
                    <Search />
                  </div>
                </div>

                {filters.search && (
                  <div
                    className={styles.ClearSearch}
                    onClick={() => {
                      if (slug) {
                        navigate(
                          '/' +
                            i18n.language +
                            '/lista' +
                            '?search=&ordering=' +
                            filters.ordering +
                            '&municipality=' +
                            filters.municipality +
                            '&province=' +
                            filters.province +
                            '&region=' +
                            filters.region +
                            '&category=' +
                            filters.app_category +
                            '&in_contest=' +
                            filters.in_contest +
                            '&only_without_pictures=' +
                            filters.only_without_pictures +
                            '&without_coordinates' +
                            filters.without_coordinates +
                            '&last_image_before_5_years=' +
                            filters.last_image_before_5_years
                        )
                        setDetail(null)
                      } else {
                        setFilters({
                          ...filters,
                          search: '',
                        })
                      }
                    }}
                  >
                    <CloseSecondary />
                  </div>
                )}
              </div>

              <Suspense
                fallback={
                  <div
                    style={{
                      height: 'calc(100% - 64px)',
                    }}
                    className="w-100 d-flex align-items-center justify-content-center"
                  >
                    <div className="loader" />
                  </div>
                }
              >
                <ListMonuments
                  detail={detail}
                  setDetail={setDetail}
                  filters={filters}
                  setFilters={setFilters}
                />
              </Suspense>
            </div>

            <div className="h-100 position-relative">
              {!detail && (
                <BlockOrdering filters={uiFilters} setFilters={setFilters} />
              )}
            </div>
          </div>

          {showLegend && <Legend />}
        </div>

        {!Number.isNaN(detail) && detail && (
          <Suspense
            fallback={
              <div className={styles.CardDetail}>
                <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                  <div className="loader" />
                </div>
              </div>
            }
          >
            <div className={styles.CardDetail}>
              {!Number.isNaN(detail) && (
                <Detail isDesktop monumentId={detail} setDetail={setDetail} />
              )}
            </div>
          </Suspense>
        )}
      </div>
    </Layout>
  )
}
